import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "../../components/reusableElementsComponents/Button";
import Loading from "../../components/reusableElementsComponents/Loading";
import loading from "../../assets/images/icons/loading.gif";
// import GoogleIcon from "../../assets/images/icons/socialMediaIcons/Google.svg";
// import FacebookIcon from "../../assets/images/icons/socialMediaIcons/Facebook.svg";
// import TwitterIcon from "../../assets/images/icons/socialMediaIcons/Twitter.svg";

import {
  FormControlLabel,
  Checkbox,
  FormControl,
  TextField,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import * as Constants from "./../../constants";
import TalentsApiService from "../../TalentsApiService";

import { inputLabelClasses } from "@mui/material/InputLabel";
import OnboardingStepper from "../../components/layoutComponents/navigation/OnboardingStepper";
import { t } from "../../content";
import { useLocale } from "../../locale";
import AuthPageChrome from "../../components/layoutComponents/authComponents/AuthPageChrome";

function RegistrationStage1() {
  const { withLocale, locale } = useLocale();

  let companyData, teamData;
  const corporate = useSelector((state) => state.teamData?.all?.[0]) || null;
  if (corporate) {
    companyData = corporate.company;
    teamData = corporate.team;
  }

  const invitationFromStore = useSelector(
    (state) => state.userData?.invitationData
  );
  const location = useLocation();
  const invitationFromUrl = Object.fromEntries(
    new URLSearchParams(location.search).entries()
  );
  const invitationData =
    invitationFromStore ||
    (invitationFromUrl.team ||
    invitationFromUrl.pool ||
    invitationFromUrl["user-token"]
      ? invitationFromUrl
      : null);
  // JSON.parse(localStorage.getItem(Constants.MAZHR_INVITATION_DATA)) || null;
  let userInvitationTeamHash, userInvitationTemporaryToken,userInvitationPoolHash;
  if (invitationData) {
    if(invitationData.team){
      userInvitationTeamHash = invitationData.team;
    }else if(invitationData.pool){
      userInvitationPoolHash = invitationData.pool;
    }
    userInvitationTemporaryToken = invitationData["user-token"];
  }

  const [isValidInvitation, setIsValidInvitation] = useState(null);

  // DECLARE STATE VARIABLES
  const [formValues, setFormValues] = useState({
    first: "",
    last: "",
    email: "",
    password: "",
    re_password: "",
    terms_conditions_agreement: true,
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [formErrors, setFormErrors] = useState({
    first: false,
    last: false,
    email: false,
    password: false,
    re_password: false,
    terms_conditions_agreement: false,
    processFailed: false,
  });
  const [formErrorMessages, setFormErrorMessages] = useState({
    first: t("validation.nameLettersOnly"),
    last: t("validation.nameLettersOnly"),
    email: t("validation.invalidEmailFormat"),
    password: t("validation.passwordInstruction"),
    re_password: t("validation.passwordsMustMatch"),
    terms_conditions_agreement: t("validation.termsRequired"),
    processFailed: t("auth.register.processFailed"),
  });
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(false);

  // ALL FUNCTIONS
  const talentsServiceObj = useMemo(() => {
    return new TalentsApiService();
  }, []);

  const navigate = useNavigate();
  const [resolvedTeamHash, setResolvedTeamHash] = useState(userInvitationTeamHash);
  const [resolvedUserToken, setResolvedUserToken] = useState(userInvitationTemporaryToken);

  useEffect(() => {
    setResolvedTeamHash(userInvitationTeamHash);
    setResolvedUserToken(userInvitationTemporaryToken);
  }, [userInvitationTeamHash, userInvitationTemporaryToken]);

  useEffect(() => {
    const applyInvitedUserProfile = (callbackData, tokenUsed) => {
      if (!callbackData) {
        return;
      }
      setFormValues((prevFormValues) => {
        const { first, last, email, ...rest } = prevFormValues;
        return {
          first: callbackData.first || first,
          last: callbackData.last || last,
          email: callbackData.email || email,
          ...rest,
        };
      });
      if (tokenUsed) {
        setResolvedUserToken(tokenUsed);
      }
      if (callbackData.team_hashes?.length && !userInvitationTeamHash) {
        setResolvedTeamHash(callbackData.team_hashes[0]);
      } else if (callbackData.team_hashes?.length && userInvitationTeamHash) {
        // `?team=` may be a temp_token rather than a team hash. Prefer a real team hash from the API.
        const hashes = callbackData.team_hashes;
        if (!hashes.includes(userInvitationTeamHash)) {
          setResolvedTeamHash(hashes[0]);
        }
      }
    };

    if (userInvitationTemporaryToken) {
      talentsServiceObj.LoadUserDataByTemporaryToken(
        userInvitationTemporaryToken,
        (callbackData) => applyInvitedUserProfile(callbackData, userInvitationTemporaryToken),
        setLoader
      );
      return;
    }

    // Legacy / misbuilt invite links put temp_token in `?team=` and use `#login`.
    // If that value identifies an invited user, prefill registration instead of bouncing to login.
    if (userInvitationTeamHash) {
      talentsServiceObj.LoadUserDataByTemporaryToken(
        userInvitationTeamHash,
        (callbackData) => applyInvitedUserProfile(callbackData, userInvitationTeamHash)
      );
    }
  }, [userInvitationTemporaryToken, userInvitationTeamHash, talentsServiceObj]);

  // If team hash or pool hash exist in the url, validate if the userTeam or userPool exist and are valid
  useEffect(() => {
    if (resolvedTeamHash && resolvedUserToken && resolvedTeamHash !== resolvedUserToken) {
      talentsServiceObj.ValidateUserInvitation(
        { temp_token: resolvedUserToken, hash_type: "team", hash: resolvedTeamHash },
        setIsValidInvitation,
        setLoader
      );
    } else if (userInvitationPoolHash && userInvitationTemporaryToken) {
      talentsServiceObj.ValidateUserInvitation(
          { temp_token: userInvitationTemporaryToken, hash_type: "pool", hash: userInvitationPoolHash },
          setIsValidInvitation,
          setLoader
      );
    }
  }, [resolvedTeamHash, resolvedUserToken, userInvitationPoolHash, userInvitationTemporaryToken, talentsServiceObj]);

  useEffect(() => {
    if (resolvedTeamHash && resolvedTeamHash !== resolvedUserToken) {
      talentsServiceObj.LoadTeamByHash(resolvedTeamHash);
    }
  }, [resolvedTeamHash, resolvedUserToken, talentsServiceObj]);

  useEffect(() => {
    setFormErrorMessages({
      first: t("validation.nameLettersOnly"),
      last: t("validation.nameLettersOnly"),
      email: t("validation.invalidEmailFormat"),
      password: t("validation.passwordInstruction"),
      re_password: t("validation.passwordsMustMatch"),
      terms_conditions_agreement: t("validation.termsRequired"),
      processFailed: t("auth.register.processFailed"),
    });
  }, [locale]);

  const emailAddressExist = (exist) => {
    if (exist) {
      setFormErrors({ ...formErrors, email: true });
      setFormErrorMessages({
        ...formErrorMessages,
        email: t("validation.emailAlreadyExists"),
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormValues({
      ...formValues,
      [name]: value,
    });

    if (!value) {
      updateFormErrorsObject(name, true);
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    let valid = false;
    if (value !== undefined && value.length > 0) {
      valid = validateFieldValues(name, value);
    }

    if (valid && name === "email" && !resolvedUserToken) {
      talentsServiceObj.EmailExist(value, emailAddressExist);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: checked,
    });
    validateFieldValues(name, checked);
  };

  const updateFormErrorsObject = (name, value) => {
    setFormErrors({
      ...formErrors,
      [name]: value,
    });
  };

  const validateFieldValues = (name, value) => {
    let valid = false;
    switch (name) {
      case "first":
      case "last":
        valid = Constants.VALID_NAME.test(value);
        if (!valid) {
          setError(true);
        }
        updateFormErrorsObject(name, !valid);
        break;
      case "email":
        valid = Constants.VALID_EMAIL.test(value);
        if (!valid) {
          setError(true);
        }
        updateFormErrorsObject(name, !valid);
        if (!valid) {
          setError(true);
        }
        break;
      case "password":
        valid = Constants.VALID_PASSWORD.test(value);
        if (!valid) {
          setError(true);
        }
        updateFormErrorsObject(name, !valid);
        break;
      case "re_password":
        valid = Constants.VALID_PASSWORD.test(value);
        if (!valid || formValues.password !== value) {
          setError(true);
          updateFormErrorsObject("re_password", true);
          setFormErrorMessages({
            ...formErrorMessages,
            re_password: t("validation.passwordsMustMatch"),
          });
        } else {
          updateFormErrorsObject("re_password", false);
        }
        break;
      case "terms_conditions_agreement":
        valid = value;
        updateFormErrorsObject("terms_conditions_agreement", !value);
        break;
      default:
        break;
    }

    return valid;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormErrors({ ...formErrors, processFailed: false });
    setLoader(true);
    let keys = Object.keys(formValues);
    let errorFound = true;
    for (let index = 0; index < keys.length; index++) {
      errorFound = !validateFieldValues(keys[index], formValues[keys[index]]);
      if (errorFound) {
        break;
      }
    }

    if (resolvedTeamHash && resolvedTeamHash !== resolvedUserToken && !errorFound) {
      talentsServiceObj
        .RegisterUser({
          ...formValues,
          hash_type: "team",
          hash: resolvedTeamHash,
        })
        .then((response) => {
          if (response.httpStatus === 200 && response.json.data) {
            localStorage.setItem(
              Constants.MAZHR_JWT_KEY,
              JSON.stringify(response.json.data)
            );
            setLoader(false);
            navigate("/assessment/");
          } else {
            setFormErrors({ ...formErrors, processFailed: true });
            if (response.json?.error?.code === 2005) {
              setFormErrorMessages({
                ...formErrorMessages,
                processFailed: response.json.error?.msg || "This email is already registered. Please log in instead.",
              });
            }
            setLoader(false);
          }
        });
    }
    else if (userInvitationPoolHash && !errorFound) {
      talentsServiceObj
          .RegisterUser({
            ...formValues,
            hash_type: "pool",
            hash: userInvitationPoolHash,
          })
          .then((response) => {
            if (response.httpStatus === 200 && response.json.data) {
              localStorage.setItem(
                  Constants.MAZHR_JWT_KEY,
                  JSON.stringify(response.json.data)
              );
              setLoader(false);
              navigate("/assessment/");
            } else {
              setFormErrors({ ...formErrors, processFailed: true });
              if (response.json?.error && response.json.error?.code === 2003) {
                setFormErrorMessages({
                  ...formErrorMessages,
                  processFailed: response.json.error?.msg || formErrorMessages.processFailed,
                });
              }
              setLoader(false);
            }
          });
    }
    else if (!errorFound) {
      setFormErrors({ ...formErrors, processFailed: true });
    } else {
      setLoader(false);
    }
  };

  // RENDER MAIN COMPONENT
  if (!loader) { 
    if (isValidInvitation === false) {
      return (
        <div className="auth-page-wrapper">
          <AuthPageChrome />
          <div className="form-wrapper">
            <h3 className="title semi-bold">{t("auth.register.invalidInvitationTitle")}</h3>
            <p className="l grey">
              {t("auth.register.invalidInvitationBody")}
              <br />
              <br />
              {t("auth.register.invalidInvitationContact")}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="auth-page-wrapper">
        <AuthPageChrome />
        <div className="form-wrapper">
          <h3 className="title semi-bold">{t("auth.register.titleDesktop")}</h3>
          <OnboardingStepper
            activeStepNumber={1}
            isTeamMember={Boolean(resolvedTeamHash || invitationData?.team)}
          />

          <form className="form" onSubmit={handleSubmit}>
            {companyData?.name && (
              <p>
                {teamData?.title
                  ? t("auth.register.invitedByCompanyTeam", {
                      company: companyData.name,
                      team: teamData.title,
                    })
                  : t("auth.register.invitedByCompany", {
                      company: companyData.name,
                    })}
              </p>
            )}
            <FormControl fullWidth>
              {formErrors.processFailed && (
                <Alert variant="outlined" severity="error">
                  {formErrorMessages.processFailed}
                </Alert>
              )}
              <TextField
                InputLabelProps={{
                  sx: {
                    [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                  },
                }}
                placeholder={t("common.placeholders.startTyping")}
                id="first"
                label={t("common.fields.firstName")}
                name="first"
                type="text"
                autoComplete="on"
                value={formValues.first}
                error={formErrors.first}
                helperText={formErrors.first && formErrorMessages.first}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
                variant="standard"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              <br />
              <TextField
                InputLabelProps={{
                  sx: {
                    [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                  },
                }}
                placeholder={t("common.placeholders.startTyping")}
                id="last"
                label={t("common.fields.lastName")}
                name="last"
                type="text"
                autoComplete="on"
                value={formValues.last}
                error={formErrors.last}
                helperText={formErrors.last && formErrorMessages.last}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
                variant="standard"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              <br />
              {resolvedUserToken && formValues.email ? (
                <TextField
                  InputLabelProps={{
                    sx: {
                      [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                    },
                  }}
                  id="email"
                  label={t("common.fields.workEmail")}
                  name="email"
                  type="email"
                  value={formValues.email}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="standard"
                  onChange={null}
                  onBlur={null}
                  disabled
                />
              ) : (
                <TextField
                  InputLabelProps={{
                    sx: {
                      [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                    },
                  }}
                  placeholder={t("common.placeholders.startTyping")}
                  id="email"
                  label={t("common.fields.workEmail")}
                  name="email"
                  type="email"
                  autoComplete="on"
                  value={formValues.email}
                  error={formErrors.email}
                  helperText={formErrors.email && formErrorMessages.email}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="standard"
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                />
              )}

              <br />
              <TextField
                InputLabelProps={{
                  sx: {
                    [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                  },
                }}
                placeholder={t("common.placeholders.startTyping")}
                id="password"
                label={t("common.fields.password")}
                name="password"
                type={passwordVisible ? "text" : "password"}
                autoComplete="on"
                value={formValues.password}
                error={formErrors.password}
                helperText={formErrors.password && formErrorMessages.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </button>
                    </InputAdornment>
                  ),
                }}
                variant="standard"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              <br />
              <TextField
                InputLabelProps={{
                  sx: {
                    [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                  },
                }}
                placeholder={t("common.placeholders.startTyping")}
                id="re_password"
                label={t("common.fields.repeatPassword")}
                name="re_password"
                type={confirmPasswordVisible ? "text" : "password"}
                autoComplete="on"
                value={formValues.re_password}
                error={formErrors.re_password}
                helperText={
                  formErrors.re_password && formErrorMessages.re_password
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <button type="button" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        {confirmPasswordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </button>
                    </InputAdornment>
                  ),
                }}
                variant="standard"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked
                    name="terms_conditions_agreement"
                    onChange={handleCheckboxChange}
                  />
                }
                label={
                  <span>
                    {t("auth.register.termsAgreement")}{" "}
                    <a
                      href="https://www.apexteam.ai/terms-of-service"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("auth.register.termsOfService")}
                    </a>{" "}
                    {t("auth.register.termsAnd")}{" "}
                    <a
                      href="https://www.apexteam.ai/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("auth.register.privacyPolicy")}
                    </a>
                  </span>
                }
                sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
              />
              {formErrors.terms_conditions_agreement && (
                <div className="input-error">
                  <p className="m-0">
                    {formErrorMessages.terms_conditions_agreement}
                  </p>
                </div>
              )}
            </FormControl>
            <div className="authButtons">
              {/* <Link className="mazhrBtn default-green white" to="/assessments"> */}
              <Button btnstyle={"mazhrBtn default-green white"} type="submit">
                {loader && !error ? (
                  <img className="loader" src={loading} alt={t("common.labels.loading")} />
                ) : (
                  <p className="m-0 light">
                    {t("auth.register.signUp")}
                  </p>
                )}
              </Button>
              {/* </Link> */}
              <Link
                className="mazhrBtn light-green"
                to={withLocale(
                  resolvedTeamHash && resolvedTeamHash !== resolvedUserToken
                    ? `/login?team=${encodeURIComponent(resolvedTeamHash)}`
                    : `/login${location.search}`
                )}
              >
                <p className="m-0 light">{t("auth.register.signIn")}</p>
              </Link>
            </div>
            {/* TEMPORARIY DISABLE SOCIAL LOGIN */}
            {/* <div className="auth-social-login">
              {content.signInSocialConnects}
              <Link
                className="social-icon"
                to={Constants.MAZHR_APP_URL + "/#"}
                target="_blank"
                rel="noreferrer"
              >
                <img className="social-icon" src={GoogleIcon} alt="Google" />
              </Link>
              <Link
                className="social-icon"
                to={Constants.MAZHR_APP_URL + "/#"}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="social-icon"
                  src={FacebookIcon}
                  alt="Facebook"
                />
              </Link>
              <Link
                className="social-icon"
                to={Constants.MAZHR_APP_URL + "/#"}
                target="_blank"
                rel="noreferrer"
              >
                <img className="social-icon" src={TwitterIcon} alt="Twitter" />
              </Link>
            </div> */}
          </form>
        </div>
      </div>
    );
  } else {
    return <Loading />;
  }
}

export default RegistrationStage1;
